import { useEffect, useRef } from 'react';
import type { Message } from '@copilotkit/shared';

import { apiSlice, TagTypes } from '@/redux/slices/apiSlice';
import { useAppDispatch } from '@/redux/store';
import { TOOL_NAMES } from '@/helpers/aiChat/tools';
import { buildToolCallIdToNameMap } from '@/helpers/aiChat/messages';
import type { GettingStartedProgress } from '@/types/aiChat';

// Maps backend tool function names to the deterministic GS UI signal they should fire.
// Mirrors the backend's _TOOL_TO_GS_STEP in getting_started_mark_complete.py —
// keep both in sync when adding steps.
// "tags" steps invalidate the relevant RTK cache so the card re-reads ground truth.
// "progress" steps write localStorage directly (same as the UI button) for steps
// backed by async/eventual state (e.g. Temporal workflows) where a re-fetch would race.
type GsSignal =
  | { type: 'tags'; tags: { type: TagTypes; id?: string }[] }
  | { type: 'progress'; update: Partial<GettingStartedProgress> };

const BACKEND_TOOL_SIGNALS: Record<string, GsSignal> = {
  add_employees: { type: 'tags', tags: [{ type: TagTypes.Employee }] },
  update_employee_role: { type: 'tags', tags: [{ type: TagTypes.Employee }] },
  // New orgs ship with a default champion for every category, so the champion
  // count can't distinguish "configured" from "untouched". Completion for this
  // step is driven by the skipItChampions flag (set by the UI button or the
  // mark tool), so mirror that here on a successful configure call.
  configure_employee_support: {
    type: 'progress',
    update: { skipItChampions: true },
  },
  // add_groups_and_apps creates both groups and apps — invalidate both so
  // the add_groups and add_applications checklist steps both refresh.
  add_groups_and_apps: {
    type: 'tags',
    tags: [
      { type: TagTypes.Group, id: 'LIST' },
      { type: TagTypes.Application },
    ],
  },
  // connect_app_integration is a frontend tool (TOOL_NAMES); use the constant.
  [TOOL_NAMES.CONNECT_APP_INTEGRATION]: {
    type: 'tags',
    tags: [{ type: TagTypes.Application }],
  },
  // send_invitations triggers a Temporal workflow — the sendAutomaticWelcomeEmails
  // flag propagates asynchronously, so a cache invalidation would race ahead and
  // still see false. Mirror the UI card: write inviteEmployees to localStorage.
  send_invitations: {
    type: 'progress',
    update: { inviteEmployees: true },
  },
};

type UseGettingStartedAgentSyncProps = {
  messages: Message[];
  updateProgress: (updates: Partial<GettingStartedProgress>) => void;
  needsHistoryRestore: boolean;
};

/**
 * Watches the CopilotKit message stream and fires deterministic GS completion
 * signals whenever a backend tool result lands.
 *
 * Lives in the always-mounted CopilotKitAgentProvider so it is immune to the
 * GettingStartedContainer unmount/remount cycle that can occur after HITL
 * approval flows, and fires even when the agent forgets to call
 * mark_getting_started_step_complete.
 *
 * Uses needsHistoryRestore to distinguish restored history from live completions:
 * - While needsHistoryRestore is true: skip (history may be mid-load).
 * - On needsHistoryRestore false→true→false transition: seed all current tool
 *   result IDs without firing, so restored completions are not replayed.
 * - Normal (needsHistoryRestore stays false throughout): fire for every new
 *   tracked tool result, including the very first one in a fresh chat.
 */
export const useGettingStartedAgentSync = ({
  messages,
  updateProgress,
  needsHistoryRestore,
}: UseGettingStartedAgentSyncProps) => {
  const dispatch = useAppDispatch();
  // Set of toolCallIds already processed — prevents re-firing on re-renders.
  const processedRef = useRef<Set<string>>(new Set());
  // Track the previous value of needsHistoryRestore to detect the restore transition.
  const prevNeedsHistoryRestoreRef = useRef(needsHistoryRestore);

  useEffect(() => {
    if (messages.length === 0) return;

    const wasRestoring = prevNeedsHistoryRestoreRef.current;
    prevNeedsHistoryRestoreRef.current = needsHistoryRestore;

    // While history is being restored, hold off — messages may be partial.
    if (needsHistoryRestore) return;

    // History restore just finished: seed all current tool result IDs so we
    // don't replay already-completed steps when new live results arrive next.
    if (wasRestoring) {
      for (const msg of messages) {
        if (msg.role === 'tool' && msg.toolCallId) {
          processedRef.current.add(msg.toolCallId);
        }
      }
      return;
    }

    // Live path: process genuinely new tool results.
    const idToName = buildToolCallIdToNameMap(messages);
    for (const msg of messages) {
      if (msg.role !== 'tool' || !msg.toolCallId) continue;
      if (processedRef.current.has(msg.toolCallId)) continue;

      const toolName = idToName.get(msg.toolCallId);
      if (!toolName) continue;

      const signal = BACKEND_TOOL_SIGNALS[toolName];
      if (!signal) continue;

      processedRef.current.add(msg.toolCallId);

      if (signal.type === 'tags') {
        dispatch(apiSlice.util.invalidateTags(signal.tags));
      } else {
        updateProgress(signal.update);
      }
    }
  }, [messages, needsHistoryRestore, dispatch, updateProgress]);
};
